import React, { FC, Dispatch, SetStateAction } from "react";
import {
  Tabs,
  Tab,
  CircularProgress,
  Button,
  Typography,
  TextareaAutosize,
} from "@mui/material";
import DealerInfo from "../DealerInfo";
import CustomerInfo from "../CustomerInfo";
import { SuccessModal, UpdateWarningModal } from "../Modals/SuccessModal";
import ServiceTeamsBtns from "../ServiceTeamsBtns";
import Uploads from "../Uploads";
import ErrModal from "../Modals/ErrModal";
import IncludeIssue from "../IncludeIssue";

import ServiceTable from "../Table/ServiceTable";
import ServiceImages from "../ServiceImages";
import FormNotes from "../../../FormNotes/FormNotes";
import TabPanel from "../../../TabPanel";
import { RepairState } from "../../../../pages/DealerZone/DealerZone";
import { styled } from "@mui/material/styles";
import useServiceSelection from "./useServiceSelection";

function a11yProps(index: number) {
  return {
    id: `full-width-tab-${index}`,
    "aria-controls": `full-width-tabpanel-${index}`,
  };
}

const StyledDivRoot = styled("div")({
  backgroundColor: "#fafafa",
  width: "100%",
  marginTop: -40,
});

const StyledDivInfo = styled("div")({
  display: "flex",
  justifyContent: "space-around",
  alignItems: "center",
  flexWrap: "wrap",
});

const StyledDivServiceTitle = styled("div")({
  padding: 10,
  backgroundColor: "#57d5f2",
  width: "100%",
  paddingLeft: 20,
  border: "1px solid lightgrey",
  marginTop: 20,
  boxShadow: "4px 4px 8px #888888",
  zIndex: 10,
  position: "relative",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

const StyledTypographyTitle = styled(Typography)({
  fontWeight: "bold",
  textDecoration: "underline",
  marginLeft: 100,
});

const StyledDivScroll = styled("div")({
  overflow: "auto",
  height: "70vh",
});

const StyledTab = styled(Tab)({
  textTransform: "none",
  fontWeight: "bold",
  fontSize: 16,
  border: "1px solid lightgrey",
});

const StyledDivRowSpace = styled("div")({
  display: "flex",
  justifyContent: "space-around",
  padding: 10,
});

const StyledDivRowCenter = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

const StyledDivRowCenterWide = styled("div")({
  display: "flex",
  justifyContent: "center",
  width: "100%",
  alignItems: "center",
  padding: 15,
});

const StyledDivCol = styled("div")({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
});

const StyledTextArea = styled(TextareaAutosize)({
  marginLeft: 100,
  marginTop: 20,
  width: "85%",
  fontWeight: "bold",
  backgroundColor: "#fafafa",
});

const StyledErr = styled(Typography)({
  color: "red",
  display: "flex",
  justifyContent: "center",
  fontWeight: "bold",
  fontSize: 20,
});

const StyledButtonGroup = styled("div")({
  padding: 20,
  display: "flex",
  justifyContent: "center",
});

const StyledButtonCancel = styled(Button)({
  color: "white",
  marginRight: 10,
});

const StyledDivLoadWrap = styled("div")({
  backgroundColor: "lightgrey",
  height: "75vh",
  position: "relative",
});

const StyledCircularProgress = styled(CircularProgress)({
  position: "absolute",
  left: "45%",
  top: "50%",
});

const StyledDivCreateServ = styled("div")({
  backgroundColor: "lightgrey",
  height: "75vh",
  position: "relative",
});

const StyledButton = styled(Button)({
  position: "absolute",
  left: "45%",
  top: "50%",
});

const StyledTypoService = styled(Typography)({
  fontWeight: "bold",
  marginLeft: 20,
  paddingTop: 20,
});

interface ServiceRepairProps {
  id: string;
  snNumber: number;
  setSnNumber: Dispatch<SetStateAction<number>>;
  getSN: () => void;
  repair: RepairState;
  setRepair: Dispatch<SetStateAction<RepairState>>;
}

const ServiceRepair: FC<ServiceRepairProps> = ({
  id,
  snNumber,
  setSnNumber,
  getSN,
  repair,
  setRepair,
}) => {
  const {
    disabled,
    handleFormChange,
    formNotes,
    handleCloseModal,
    modalEmailErr,
    handleOpenWarnModal,
    openModalWarn,
    openModal,
    editSR,
    createSR,
    handleUploadSubmit,
    handleChange,
    handleIncludeChange,
    handleInfoChange,
    handleChangeAdmin,
    handleToggle,
    serviceTeam,
    toggleEmail,
    loadUpload,
    loading,
    customerInfo,
    uploadURLs,
    value,
    submissionErr,
    openErrModal,
    error,
    adminDealer,
    viewSR,
    isFetching,
    data,
    theme,
    handleCloseWarnModal,
    setOpenErrModal,
    setAdminDealer,
    setServiceTeam,
    resetServiceRepair,
    dealerList,
    handleDealerDropdown
  } = useServiceSelection(id, snNumber, setSnNumber, repair, setRepair);

  return (
    <>
      <SuccessModal
        modalEmailErr={modalEmailErr}
        open={openModal}
        handleClose={handleCloseModal}
        isUpdated={repair.status === "Submitted"}
        toggleEmail={toggleEmail}
      />

      <UpdateWarningModal
        open={openModalWarn}
        handleClose={handleCloseWarnModal}
        srFunction={editSR}
        toggle={toggleEmail}
        handleToggle={handleToggle}
        loading={loading}
      />

      <ErrModal
        modalText={submissionErr}
        setOpen={setOpenErrModal}
        open={openErrModal}
      />

      <StyledDivRoot>
        <Tabs
          value={value}
          onChange={handleChange}
          indicatorColor="primary"
          textColor="inherit"
          style={{ backgroundColor: "#fafafa", paddingLeft: 40 }}
          aria-label="full width tabs example"
        >
          <StyledTab
            style={{ backgroundColor: value === 0 ? "white" : "#e1dfe0" }}
            label={repair.status ? "View Service Repair" : "New Request"}
            {...a11yProps(0)}
          />
          <StyledTab
            style={{ backgroundColor: value === 1 ? "white" : "#e1dfe0" }}
            label="Previous Service"
            {...a11yProps(1)}
          />
        </Tabs>

        <TabPanel
          value={value}
          index={0}
          dir={theme.direction}
          style={{ border: "2px solid lightgrey" }}
        >
          {snNumber && !loading ? (
            <>
              <StyledDivServiceTitle>
                <StyledTypographyTitle variant="h6">
                  Service Repair Form
                </StyledTypographyTitle>
                <Typography>Service Number: {snNumber}</Typography>
              </StyledDivServiceTitle>
              <StyledDivScroll>
                <StyledDivRowSpace>
                  {customerInfo.createdBy && <div />}

                  <Typography>
                    <span style={{ fontWeight: "bold" }}>
                      For replacement parts please email us at:
                    </span>{" "}
                    customerservice@agmillworks.com
                  </Typography>

                  {customerInfo.createdBy && (
                    <StyledDivRowCenter>
                      <Typography style={{ fontSize: 12 }}>
                        <span style={{ fontWeight: "bold", fontSize: 12 }}>
                          Created By:
                        </span>{" "}
                        {customerInfo.createdBy}
                      </Typography>
                    </StyledDivRowCenter>
                  )}
                </StyledDivRowSpace>

                <StyledDivInfo>
                  <CustomerInfo
                    info={customerInfo}
                    handleChange={handleInfoChange}
                    disabled={disabled}
                  />
                  <DealerInfo
                    handleChangeAdmin={handleChangeAdmin}
                    adminDealer={adminDealer}
                    repairStatus={repair.status}
                    info={customerInfo}
                    handleChange={handleInfoChange}
                    data={data}
                    isFetching={isFetching}
                    disabled={disabled}
                    setAdminDealer={setAdminDealer}
                    dealerList={dealerList}
                    handleDealerDropdown={handleDealerDropdown}
                  />
                </StyledDivInfo>

                {(data.signInRole === "Admin" ||
                  data.signInRole === "Inside Sales") && (
                  <ServiceTeamsBtns
                    team={serviceTeam}
                    setTeam={setServiceTeam}
                    isEdit={repair.status ? true : false}
                    snNumber={snNumber}
                  />
                )}

                <StyledDivCol
                  style={{
                    position: "relative",
                    marginBottom: 20,
                    marginTop: 20,
                  }}
                >
                  <Typography style={{ fontWeight: "bold" }}>
                    Service Issue <span style={{ fontSize: 20, color: "red" }}>*</span>
                  </Typography>
                  <StyledTextArea
                    disabled={disabled}
                    placeholder="Describe Issue..."
                    name="issue"
                    value={customerInfo.issue}
                    onChange={handleInfoChange}
                    minRows={10}
                  />
                </StyledDivCol>

                {(data.signInRole === "Admin" ||
                  data.signInRole === "Inside Sales" ||
                  data.signInRole === "Outside Sales") && (
                  <FormNotes
                    formNotes={formNotes}
                    handleChange={handleFormChange}
                    isService={true}
                    serviceId={repair.id}
                    role={data.signInRole}
                  />
                )}

                {repair.status !== "Scheduled" ? (
                  <>
                    <Uploads
                      loading={loadUpload}
                      handleSubmit={handleUploadSubmit}
                      uploadURLs={uploadURLs}
                    />

                    {repair.status &&
                      repair.id &&
                      (uploadURLs.u1 || uploadURLs.u2 || uploadURLs.u3) && (
                        <ServiceImages urls={uploadURLs} />
                      )}

                    {disabled ? null : (
                      <>
                        <StyledDivRowCenterWide>
                          <IncludeIssue
                            include={customerInfo.includeIssueInEmail || ""}
                            handleChange={handleIncludeChange}
                          />
                        </StyledDivRowCenterWide>

                        {error && snNumber && (
                          <StyledErr>All Fields Required</StyledErr>
                        )}

                        <StyledButtonGroup>
                          {repair.status && (
                            <StyledButtonCancel
                              onClick={resetServiceRepair}
                              variant="contained"
                              color="customGrey"
                            >
                              Cancel Update
                            </StyledButtonCancel>
                          )}

                          <Button
                            onClick={
                              repair.status ? handleOpenWarnModal : createSR
                            }
                            variant="contained"
                            color="primary"
                            disabled={error || repair.status === "Completed"}
                          >
                            {repair.status ? "Update" : "Submit"} Service Repair
                          </Button>
                        </StyledButtonGroup>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {(uploadURLs.u1 || uploadURLs.u2 || uploadURLs.u3) && (
                      <ServiceImages urls={uploadURLs} />
                    )}

                    {(data.signInRole === "Admin" ||
                      data.signInRole === "Inside Sales") && (
                      <>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            width: "100%",
                            alignItems: "center",
                            padding: 15,
                          }}
                        >
                          <IncludeIssue
                            include={customerInfo.includeIssueInEmail || ""}
                            handleChange={handleIncludeChange}
                          />
                        </div>

                        {error && snNumber && (
                          <StyledErr>All Fields Required</StyledErr>
                        )}

                        <StyledButtonGroup>
                          {repair.status && (
                            <StyledButtonCancel
                              onClick={resetServiceRepair}
                              variant="contained"
                              color="customGrey"
                            >
                              Cancel Update
                            </StyledButtonCancel>
                          )}

                          <Button
                            onClick={
                              repair.status ? handleOpenWarnModal : createSR
                            }
                            variant="contained"
                            color="primary"
                            disabled={error}
                          >
                            {repair.status ? "Update" : "Submit"} Service Repair
                          </Button>
                        </StyledButtonGroup>
                      </>
                    )}
                  </>
                )}
              </StyledDivScroll>
            </>
          ) : snNumber && loading ? (
            <StyledDivLoadWrap>
              <StyledCircularProgress />
            </StyledDivLoadWrap>
          ) : (
            <StyledDivCreateServ>
              <StyledButton
                onClick={getSN}
                variant="contained"
                size="large"
                color="primary"
              >
                Create Service Repair
              </StyledButton>
            </StyledDivCreateServ>
          )}
        </TabPanel>
        <TabPanel value={value} index={1} dir={theme.direction}>
          <StyledTypoService variant="h4">Service</StyledTypoService>

          <ServiceTable viewSR={viewSR} />
        </TabPanel>
      </StyledDivRoot>
    </>
  );
};

export default ServiceRepair;
